import ApiError from '@/app/api/ApiError';
import {thresolds} from '@/cst/utils';
import crypto from 'crypto';
import {cloneDeep} from 'lodash';
import lzjs from 'lzjs';
import moment from 'moment';
import {Locale} from 'next-intl';
import {getLocale, getTranslations} from 'next-intl/server';
import {notFound} from 'next/navigation';
import {env} from 'process';
import Height from '../modules/Height';
import getLayout from './layouts';
import getModuleData from './modules';

export async function htmlParseOptions(
  content: string,
  locale: Locale,
  options: any
) {
  const moduleData = await getModuleData(content, locale, options);
  return {
    replace: (domNode: any) => {
      if (domNode.type === 'text') {
        const text = domNode.data;
        if (/\[module:(.+)\]/g.test(text)) {
          const match = text.match(/\[module:(.+)\]/);
          if (match && match[1]) {
            const moduleName = match[1].split('=')[0];
            const moduleItems = moduleData?.[match[1]] || [];
            const moduleOptionKey = match[1].split('=')[1] || '';
            const moduleOption = cloneDeep(options.meta?.[moduleOptionKey]);

            const moduleSlug = match[1].split('=')[1] || '';
            return getLayout({
              locale,
              moduleSlug,
              moduleName,
              moduleData: moduleItems,
              moduleOption,
              options
            });
          }
        }

        if (/\[h=(.+)\]/g.test(text)) {
          const match = text.match(/\[h=(.+)\]/);
          if (match && match[1]) {
            const moduleSlug = match[1] || '';
            return <Height {...{moduleSlug}} />;
          }
        }
      }
      return null;
    }
  };
}

export function replacePToDiv(content: string) {
  return content.replace(/<p([^>]*)>([\s\S]*?)<\/p>/g, '<div$1>$2</div>');
}

export function target(href: string) {
  const url = href.trim();
  if (url && url.startsWith('https://www.aswatson.com')) {
    return '_self';
  }
  if (url && (url.startsWith('http') || url.startsWith('/uploads'))) {
    return '_blank';
  }
  return '_self';
}

export function title(...title: string[]) {
  return title
    .filter((t) => t !== '' || t !== null || t !== undefined)
    .join(' | ');
}

export function formHash(...h: string[]) {
  const md5 = crypto.createHash('md5');
  const secret = env.SECRET_KEY;
  return [md5.update(secret + h.join('')).digest('hex'), ...h].join('.');
}
export function genHash() {
  const random = Math.random().toString(36).substring(2, 15);
  const now = new Date().getTime();
  return formHash(random, now.toString(36));
}

const tokenCache = new Map();
export function validHash(hash: string, options?: any) {
  const hashArr = hash.split('.');
  const random = hashArr[1];
  const ttl = options?.ttl || 1000 * 60 * 60 * 24; // 1 day

  const expectedHash = formHash(random, hashArr[2] ?? '');
  if (options.human) {
    if (hash.split('.').slice(0, 3).join('.') != expectedHash) {
      return {valid: false, message: 'Invalid hash: format unmatch (h)'};
    }
    if (!validHuman(hash)) {
      return {valid: false, message: 'Invalid hash: human rule'};
    }
  } else {
    if (hash != expectedHash) {
      return {valid: false, message: 'Invalid hash: format unmatch'};
    }
  }

  // 1 hours
  const timestamp = parseInt(hashArr[2] ?? '', 36);
  if (new Date().getTime() - timestamp > ttl) {
    throw new ApiError('TOKEN_EXPIRED');
  }

  // check cached token
  const isOneTime = !options?.nonOneTime;
  if (tokenCache.get(hash) && isOneTime) {
    return {valid: false, message: 'Token used'};
  } else {
    // cache token
    tokenCache.set(hash, {hash, timestamp});
  }

  // delete expired token
  const now = new Date().getTime();
  tokenCache.forEach((v, k) => {
    if (now - v.timestamp > ttl) {
      tokenCache.delete(k);
    }
  });

  return {valid: true, message: 'Valid hash'};
}

export function validHuman(hash: string) {
  const oHash = hash.split('.').slice(0, 3).join('.');
  const encrypted = hash.replace(oHash + '.', '');
  try {
    const decipher = crypto.createDecipheriv(
      'aes-256-cbc',
      oHash.substring(0, 32),
      oHash.substring(0, 16)
    );
    const buffer = Buffer.from(encrypted, 'hex');
    let decrypted = decipher.update(buffer);
    decrypted = Buffer.concat([decrypted, decipher.final()]);
    const actions = JSON.parse(lzjs.decompress(decrypted.toString('utf8')));

    let valid = false;
    Object.entries(thresolds).map(([k, v]) => {
      if (!valid && actions?.[k]?.length == v) {
        valid = true;
      }
    });
    return valid;
  } catch (e: any) {
    console.error(e, {hash});
    return false;
  }
}

export const formPreviewHash = (...h: string[]) => {
  const secret = process.env.PREVIEW_SECRET ?? 'preview';
  const str = h.join('') + secret;
  let hash = 5381;
  let i = str.length;
  while (i) {
    hash = (hash * 33) ^ str.charCodeAt(--i); // hash * 33 + charCode
  }
  return (hash >>> 0).toString(16); // convert to unsigned 32-bit integer and to hexadecimal
};

export async function validatePreview(k: string) {
  const hash = await formPreviewHash(k.split('.')[0]);
  if (hash !== k.split('.')[1]) {
    notFound();
  }
}

export const formEditModeHash = (...h: string[]) => {
  const secret = process.env.STRAPI_ADMIN_EDIT_MODE_SECRET ?? 'default-secret';
  const str = h.join('') + secret;
  let hash = 5381;
  let i = str.length;
  while (i) {
    hash = (hash * 33) ^ str.charCodeAt(--i); // hash * 33 + charCode
  }
  return (hash >>> 0).toString(16); // convert to unsigned 32-bit integer and to hexadecimal
};

export function validateEditMode(k: string) {
  try {
    if (!k || k.split('.').length < 2) {
      return false;
    }
    const hash = formEditModeHash(k.split('.')[0]);
    if (hash !== k.split('.')?.[1]) {
      return false;
    }
    const d = parseInt(k.split('.')[0], 36);
    const t = moment().subtract(1, 'day').unix();
    if (d < t) {
      return false;
    }
    return true;
  } catch (e: any) {
    return false;
  }
}

export function abstract(
  content: string,
  locale: Locale,
  maxDescriptionLength: number
) {
  const words = content
    .replace(/\[module:[^\]]+\]/g, ' ')
    .replace(/nbsp;/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .split(' ')
    .filter((str) => str)
    .join(' ')
    .replace(/&amp;/g, '&')
    .replace(/[.,\/#!$%\^&\*:{}=\-_`~()]/g, ' ')
    .split(/\s+/);
  if (locale === 'en') {
    return (
      words.slice(0, maxDescriptionLength).join(' ') +
      (maxDescriptionLength < words.length ? '...' : '')
    );
  } else {
    return (
      words
        .map((w) => {
          return w;
        })
        .join('')
        .substring(0, maxDescriptionLength) +
      (maxDescriptionLength < content.length ? '...' : '')
    );
  }
}

export async function jsonLd(pageDetail: any, options: any = {}) {
  const jsonLd = [];
  try {
    const locale = await getLocale();
    const t = await getTranslations({locale, namespace: 'jsonLd'});
    const slugs = pageDetail.slug.split('/');
    const url = env.APP_URL + (locale != 'en' ? '/' + locale : '');

    const post_meta = pageDetail.post_meta ?? {};
    const image = [
      ...(options?.images || []),
      ...((
        Object.values(post_meta).find((v: any) => {
          return v.type === 'note';
        }) as any
      )?.items
        .map((i: any) => (typeof i?.image == 'string' ? i.image : null))
        .filter(Boolean) ?? [])
    ]
      .map((i: any) => {
        return typeof i == 'string'
          ? (i.trim().startsWith('http') ? '' : env.APP_URL) + i.trim()
          : '';
      })
      .filter(Boolean);
    if (slugs[0] == 'home') {
      // OrganizationJsonLd
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        url,
        logo: env.APP_URL + '/img/asw-185-logo-' + locale + '.png',
        name: t('name'),
        description: t('description'),
        email: 'enquiry@aswatson.com',
        telephone: '+852 2606 8833',
        address: {
          '@type': 'PostalAddress',
          postalCode: '',
          streetAddress: t('streetAddress'),
          addressLocality: t('addressLocality'),
          addressCountry: t('addressCountry'),
          addressRegion: t('addressRegion')
        }
      });
    } else if (['news', 'sustainability'].includes(pageDetail.post_type)) {
      // newsJsonLd
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: pageDetail.title,
        image,
        datePublished: pageDetail.posted_at,
        dateModified: pageDetail.updatedAt
      });
    } else {
      // webJsonLd
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        url: url + '/' + pageDetail.slug
      });

      if (slugs.length > 0) {
        const breadcrumbJsonLd = {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: slugs.map((slug: string, index: number) => {
            return {
              '@type': 'ListItem',
              position: index + 1,
              name: slug,
              item: url + '/' + slugs.slice(0, index + 1).join('/')
            };
          })
        };
        jsonLd.push(breadcrumbJsonLd);
      }
    }
  } catch (e: any) {
    console.error(e);
  }
  return jsonLd.length == 1 ? jsonLd[0] : jsonLd;
}

const ALGORITHM = 'aes-256-cbc';

const getSessionKey = () => {
  const secret =
    env.SECRET_KEY || 'elabasia-asw-corporate-website-default-secret';
  return crypto.createHash('sha256').update(secret).digest();
};

const getSessionIv = () => {
  const secret =
    env.SECRET_KEY || 'elabasia-asw-corporate-website-default-secret';
  return crypto.createHash('md5').update(secret).digest();
};

export function encryptSession(data: any): string {
  const text = JSON.stringify(data);
  const cipher = crypto.createCipheriv(
    ALGORITHM,
    getSessionKey(),
    getSessionIv()
  );
  let encrypted = cipher.update(text, 'utf8', 'hex');
  encrypted += cipher.final('hex');
  return encrypted;
}

export function decryptSession(encryptedText: string): any {
  try {
    const decipher = crypto.createDecipheriv(
      ALGORITHM,
      getSessionKey(),
      getSessionIv()
    );
    let decrypted = decipher.update(encryptedText, 'hex', 'utf8');
    decrypted += decipher.final('utf8');
    return JSON.parse(decrypted);
  } catch (e) {
    return null;
  }
}

/**
 * Validates password strength and format requirements.
 * Evaluates password based on length and variety of character classes.
 * Minimum 8 characters required.
 *
 * Returns { strength: 'weak' | 'mid' | 'strong', pass: boolean }
 */
export function passwordStrength(password: string): {
  strength: 'weak' | 'mid' | 'strong';
  pass: boolean;
} {
  if (!password) {
    return {strength: 'weak', pass: false};
  }

  const length = password.length;

  // Rule 1: minimum length 8 characters
  if (length < 8) {
    return {strength: 'weak', pass: false};
  }

  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);

  // Digit-only passwords
  const onlyDigits = /^[0-9]+$/.test(password);

  // Count distinct character classes
  let varietyCount = 0;
  if (hasLowercase) varietyCount++;
  if (hasUppercase) varietyCount++;
  if (hasDigit) varietyCount++;
  if (hasSymbol) varietyCount++;

  let strength: 'weak' | 'mid' | 'strong' = 'weak';

  if (onlyDigits) {
    // Digit-only passwords are always weak, regardless of length
    strength = 'weak';
  } else if (varietyCount <= 1) {
    // Only one character class is weak unless extremely long
    if (length >= 16) {
      strength = 'mid';
    } else {
      strength = 'weak';
    }
  } else if (varietyCount === 2) {
    // Two character classes (e.g., letters + numbers)
    if (length >= 12) {
      strength = 'strong';
    } else {
      strength = 'mid';
    }
  } else if (varietyCount === 3) {
    // Three character classes
    if (length >= 10) {
      strength = 'strong';
    } else {
      strength = 'mid';
    }
  } else {
    // Four character classes
    strength = 'strong';
  }

  const pass = strength !== 'weak';
  return {strength, pass};
}
